/**
 * scene.ts — builds the Three.js scene: arena geometry, lights, camera, post.
 *
 * Arena meshes are generated from the shared ARENA description the server builds
 * its Rapier colliders from, so visuals and collision cannot drift.
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ARENA, ARENA_WALLS } from '@dueler/shared';
import { MORTAR_RELIEF, createDungeon } from './dungeon.js';
import { createEnvironment } from './env.js';

export interface Stage {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  composer: EffectComposer;
  /** Toggles the arena collider wireframe overlay (the K keybind). */
  setColliderWireframe(on: boolean): void;
  /** Animates the room — torch flicker. Pass render time, not wall time. */
  update(now: number): void;
  /** Call on resize; also called once at construction. */
  resize(): void;
  /** Renders one frame through the post stack. */
  render(): void;
  dispose(): void;
}

export function createStage(canvasParent: HTMLElement): Stage {
  const scene = new THREE.Scene();
  // Underground: no sky, and the far end of the hall falls away into the dark
  // well before the wall. Tuned so opponents across the hall stay readable.
  const GLOOM = 0x14110c;
  scene.background = new THREE.Color(GLOOM);
  scene.fog = new THREE.Fog(GLOOM, 20, 78);

  const camera = new THREE.PerspectiveCamera(70, 1, 0.1, 200);
  camera.position.set(0, 12, 20);
  camera.lookAt(0, 1, 0);

  // Composer RTs aren't MSAA'd — skip antialias and cap DPR. The fidelity pass
  // had pushed both (DPR 2 + SSAO + soft 2k shadows) and tanked mid-range GPUs.
  const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.15;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  canvasParent.appendChild(renderer.domElement);

  scene.environment = createEnvironment(renderer) ?? null;

  scene.add(new THREE.HemisphereLight(0x5f7392, 0x2a1f14, 1.15));
  scene.add(new THREE.AmbientLight(0x3d3527, 1.1));

  // Grate shaft — single key shadow caster for the duel floor.
  const shaft = new THREE.DirectionalLight(0xffdcaa, 1.15);
  shaft.position.set(7, 24, 5);
  shaft.castShadow = true;
  shaft.shadow.mapSize.set(1024, 1024);
  shaft.shadow.bias = -0.0004;
  shaft.shadow.normalBias = 0.02;
  const extent = ARENA.size * 0.55;
  shaft.shadow.camera.left = -extent;
  shaft.shadow.camera.right = extent;
  shaft.shadow.camera.top = extent;
  shaft.shadow.camera.bottom = -extent;
  shaft.shadow.camera.near = 4;
  shaft.shadow.camera.far = 50;
  scene.add(shaft);

  const disposables: { dispose(): void }[] = [];

  const dungeon = createDungeon();
  scene.add(dungeon.object);
  disposables.push(...dungeon.disposables);

  // Mortar behind masonry — pulled back so joints read as recessed.
  const wallMat = new THREE.MeshStandardMaterial({ color: 0x23201a, roughness: 1 });
  disposables.push(wallMat);
  for (const wall of ARENA_WALLS) {
    const [hx, hy, hz] = wall.halfExtents;
    const geo = new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
    const mesh = new THREE.Mesh(geo, wallMat);
    const outward = Math.sign(wall.pos[0]) || 0;
    const outwardZ = Math.sign(wall.pos[2]) || 0;
    mesh.position.set(
      wall.pos[0] + outward * MORTAR_RELIEF,
      wall.pos[1],
      wall.pos[2] + outwardZ * MORTAR_RELIEF,
    );
    mesh.receiveShadow = true;
    scene.add(mesh);
    disposables.push(geo);
  }

  const colliders = new THREE.Group();
  colliders.visible = false;
  scene.add(colliders);
  const colliderMat = new THREE.LineBasicMaterial({ color: 0x59ff9f });
  disposables.push(colliderMat);
  {
    const groundGeo2 = new THREE.BoxGeometry(ARENA.size, 1, ARENA.size);
    const groundEdges = new THREE.EdgesGeometry(groundGeo2);
    const ground2 = new THREE.LineSegments(groundEdges, colliderMat);
    ground2.position.set(0, -0.5, 0);
    colliders.add(ground2);
    disposables.push(groundGeo2, groundEdges);

    for (const wall of ARENA_WALLS) {
      const [hx, hy, hz] = wall.halfExtents;
      const box = new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
      const edges = new THREE.EdgesGeometry(box);
      const lines = new THREE.LineSegments(edges, colliderMat);
      lines.position.set(wall.pos[0], wall.pos[1], wall.pos[2]);
      colliders.add(lines);
      disposables.push(box, edges);
    }
  }

  // ---- post: light bloom only. SSAO was a full-screen AO pass every frame
  // and cost more than the armour read it bought in a torchlit hall.
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));

  const bloomPass = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.22, 0.5, 0.85);
  composer.addPass(bloomPass);
  composer.addPass(new OutputPass());

  const resize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h);
    // Half-res bloom is enough for torch glow and much cheaper.
    composer.setSize(w, h);
    bloomPass.resolution.set(Math.floor(w / 2), Math.floor(h / 2));
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  resize();

  return {
    scene,
    camera,
    renderer,
    composer,
    setColliderWireframe: (on: boolean) => {
      colliders.visible = on;
    },
    update: (now: number) => dungeon.update(now),
    resize,
    render: () => composer.render(),
    dispose: () => {
      for (const d of disposables) d.dispose();
      composer.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}
